'use client';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { TelegramUserMeResponseDto, useTelegramSupportContactsGetSubjects } from '@/generated';
import { TextAreaInput } from '@/components/ui/textarea-input';
import { TextInput } from '@/components/ui/text-input';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { PhoneNumberInputAdapter } from '@/app/[lang]/(main)/components/shared/form/PhoneNumberRegistration/adapter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LockedField } from './LockedField';
import ReCAPTCHA from 'react-google-recaptcha';
import { Link } from '../PrivateLink/ServerLink';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';

export type FormValues = {
  name: string;
  email: string; // your custom Email input will bind here
  phone: string; // your custom Phone input will bind here
  type: string;
  comment: string;
  countryId?: string | undefined;
  prefix?: string | undefined;
  token: string;
};

type PropsLogined = {
  userData: TelegramUserMeResponseDto;
  defaultComment?: string;
  isLogined: true;
};

type PropsNotLogined = {
  userData: undefined;
  defaultComment?: string;
  isLogined: false;
};

type Props = (PropsLogined | PropsNotLogined) & { onSubmit: (val: FormValues) => void };

const defaultEmptyValues = {
  name: '',
  email: '',
  phone: '',
  type: '',
  comment: '',
  countryId: '',
  prefix: '',
  token: '',
};

export default function SupportForm({ userData, isLogined, defaultComment = '', onSubmit: sendReport }: Props) {
  const t = useTranslations('supportPage');
  const locale = useLocale();
  const defaultValues = useMemo(() => {
    if (!isLogined) {
      return defaultEmptyValues;
    }

    return {
      name: userData.firstName || '',
      email: userData.email || '',
      phone: userData.phoneNumber || '',
      type: '',
      comment: defaultComment,
      countryId: '',
      prefix: '',
      token: '',
    };
  }, [userData, isLogined, defaultComment]);

  const schema = useMemo(() => {
    const scheme = yup
      .object({
        name: yup.string().trim().required(t('required_error')),
        email: yup.string().email(t('required_error')).required(t('required_error')),
        phone: yup.string().trim().required(),
        countryId: yup.string().optional(),
        prefix: yup.string().optional(),
        type: yup.string().required(t('required_error')),
        comment: yup.string().trim().required(t('required_error')),
        token: yup.string().required(t('required_error')),
      })
      .required();
    return scheme;
  }, [t]);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const onSubmit = async (data: FormValues) => {
    sendReport(data);
    reset(defaultValues);
  };

  const { data: subjects = [] } = useTelegramSupportContactsGetSubjects();

  const onPhoneInputChange = useCallback(
    (newCountryId: string, newPhoneNumber: string, newPrefix?: string) => {
      setValue('countryId', newCountryId, { shouldValidate: true });
      setValue('phone', newPhoneNumber, { shouldValidate: true });
      setValue('prefix', newPrefix || '');
    },
    [setValue],
  );

  const handleVerify = useCallback(
    (token: string) => {
      setValue('token', token, { shouldValidate: true });
    },
    [setValue],
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='w-full mx-auto bg-white rounded-[12px] lg:shadow-[0_3px_12px_0_rgba(40,65,155,0.12)] p-[24px] flex flex-col gap-[24px]'
    >
      <h2 className='text-lg font-semibold'>{t('title')}</h2>
      <p className='text-sm text-gray-600'>
        {t('lead_1')}
        <Link className='text-green-600 underline inline-block ms-[2px]' href='/helppage'>
          {t('lead_link_text')}
        </Link>
        .
        <br />
        {t('lead_2')}
      </p>

      {/* Name */}
      <div className='flex flex-col gap-[12px] md::gap-[24px] md:flex-row md:items-center'>
        <Label className='text-sm font-medium w-[28%] text-text-disabled-day'>{t('name')}</Label>
        {isLogined && userData.firstName ? (
          <LockedField value={watch('name') as unknown as string} tooltipText={t('locked_tooltip')} />
        ) : (
          <TextInput
            withAutoScroll
            label={t('name_placeholder')}
            defaultValue={watch('name') as unknown as string}
            {...register('name')}
            error={errors.name?.message}
          />
        )}
      </div>

      <div className='flex flex-col gap-[12px] md::gap-[24px] md:flex-row md:items-center '>
        <Label className='text-sm font-medium w-[28%] text-text-disabled-day'>{t('email')}</Label>

        {isLogined && userData.email ? (
          <LockedField value={watch('email') as unknown as string} tooltipText={t('locked_tooltip')} />
        ) : (
          <TextInput
            className='w-full'
            label={t('email')}
            leftIcon={<Image src='/assets/icons/mail.svg' width={24} height={24} alt='' />}
            ltrField
            type='email'
            defaultValue={watch('email') as unknown as string}
            {...register('email')}
            error={errors.email?.message}
          />
        )}
      </div>

      <div className='flex flex-col gap-[12px] md::gap-[24px] md:flex-row md:items-center '>
        <Label className='text-sm font-medium w-[28%] text-text-disabled-day'>{t('phone')}</Label>
        {isLogined && userData.phoneNumber ? (
          <LockedField value={watch('phone') as unknown as string} tooltipText={t('locked_tooltip')} />
        ) : (
          <PhoneNumberInputAdapter
            shouldAutoFetchCountry={true}
            countryId={watch('countryId')}
            phoneNumber={watch('phone')}
            onChange={onPhoneInputChange}
            ltrField
          />
        )}
      </div>

      {/* Type */}
      <div className='flex flex-col gap-[12px] md::gap-[24px] md:flex-row md:items-center '>
        <Label className='text-sm font-medium md:w-[28%] w-full text-text-disabled-day'>{t('type')}</Label>
        <Select value={watch('type') || undefined} onValueChange={(value) => setValue('type', value)}>
          <SelectTrigger className='w-full h-[50px]! border-primary-stroke-day bg-white text-[16px]'>
            <SelectValue placeholder={t('type_placeholder')} />
          </SelectTrigger>
          <SelectContent position='popper'>
            {subjects.map(({ id, name }) => (
              <SelectItem key={id} value={id}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Comment */}
      <div className='flex flex-col gap-[12px] md::gap-[24px] md:flex-row md:items-center '>
        <Label className='text-sm font-medium md:w-[28%] w-full text-text-disabled-day'>{t('comment')}</Label>
        <TextAreaInput
          withAutoScroll
          minCounterLength={5000}
          maxCounterLength={5000}
          label={t('comment_placeholder')}
          defaultValue={watch('comment')}
          {...register('comment')}
          error={errors.comment?.message}
        />
      </div>

      <div className='flex flex-col gap-[12px] md::gap-[24px] md:flex-row md:items-center'>
        <div className='md:w-[28%]'></div>
        <div className='w-full flex flex-col gap-[4px]'>
          <ReCAPTCHA
            key={locale}
            hl={locale === 'he' ? 'iw' : locale}
            sitekey={process.env.CAPTCHA_KEY}
            onChange={handleVerify}
          />
          <FieldError error={errors.token?.message} />
        </div>
      </div>

      <div className='flex flex-col gap-[12px] md::gap-[24px] md:flex-row md:items-center'>
        <div className='md:w-[28%]'></div>
        <Button type='submit' disabled={isSubmitting} className='h-auto py-2 w-full self-end'>
          {isSubmitting ? t('sending') : t('submit')}
        </Button>
      </div>
    </form>
  );
}

function FieldError({ error }: { error?: string }) {
  if (!error) return null;
  return <p className='text-sm text-red-600'>{error}</p>;
}
